import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Car, Clock, Shield, AlertCircle, Eye, TrendingDown } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { BidHistoryGraph } from './shared/BidHistoryGraph';
import { RequestDetailsModal } from './RequestDetailsModal';

interface InsuranceRequest {
  id: string;
  name: string;
  age: number;
  date_of_birth: string;
  lowest_bid: number | null;
  email: string;
  phone: string;
  address: string;
  car_make: string;
  car_model: string;
  car_year: number;
  registration: string;
  mileage: number;
  coverage_type: string;
  no_claims_years: number;
  bid_status: 'awaiting_bids' | 'bidding_in_progress' | 'bidding_complete' | 'no_bids';
  created_at: string;
  expires_at: string;
  bids: {
    amount: number;
    created_at: string;
    status: string;
  }[];
}

interface InsuranceRequestsListProps {
  refreshKey?: number;
}

export function InsuranceRequestsList({ refreshKey = 0 }: InsuranceRequestsListProps) {
  const [requests, setRequests] = useState<InsuranceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const [now, setNow] = useState(new Date());
  const [selectedRequest, setSelectedRequest] = useState<InsuranceRequest | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState<number | null>(null);

  const handleCancelRequest = async () => {
    if (!selectedRequest) return;
    
    try {
      setIsCancelling(true);
      
      const { error } = await supabase
        .from('insurance_requests')
        .update({
          status: 'expired',
          bid_status: 'no_bids'
        })
        .eq('id', selectedRequest.id);

      if (error) throw error;
      
      // Refresh the requests list
      await fetchRequests();
      setSelectedRequest(null);
    } catch (err) {
      console.error('Error cancelling request:', err);
      alert('Failed to cancel request. Please try again.');
    } finally {
      setIsCancelling(false);
    }
  };

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Set up auto-refresh for active requests
  useEffect(() => {
    if (requests.some(req => new Date(req.expires_at) > new Date())) {
      // Refresh every 30 seconds if there are active requests
      const interval = window.setInterval(fetchRequests, 30000);
      setRefreshInterval(interval);
      return () => clearInterval(interval);
    } else if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }
  }, [requests]);

  // Function to calculate time remaining
  const getTimeRemaining = (expiresAt: string): string => {
    const expiry = new Date(expiresAt);
    const diff = expiry.getTime() - now.getTime();
    
    if (diff <= 0) return 'expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s remaining`;
    }
    return `${seconds}s remaining`;
  };

  // Helper function to format dates in UK style
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  useEffect(() => {
    if (!user) {
      setLoading(false);
      setRequests([]);
      return;
    }

    fetchRequests();
  }, [user, refreshKey]);

  async function fetchRequests() {
    try {
      setError(null);
      setLoading(true);
      
      const { data, error } = await supabase
        .from('insurance_requests')
        .select(`
          *,
          bids!left (
            amount,
            insurer_id,
            created_at,
            status
          )
        `)
        .eq('email', user?.email)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Process the data to get lowest bid
      const processedData = (data || []).map(request => {
        const allBids = request.bids?.filter(bid => bid) || [];
        const activeBids = allBids.filter(bid => bid.status === 'active');
        const lowestBid = activeBids.length > 0 
          ? Math.min(...activeBids.map(bid => bid.amount))
          : null;
        // Count unique insurers from all bids (both active and withdrawn)
        const uniqueInsurerIds = new Set(allBids.map(bid => bid.insurer_id).filter(Boolean));
        const uniqueInsurers = uniqueInsurerIds.size;

        return {
          ...request,
          lowest_bid: lowestBid,
          unique_insurers: uniqueInsurers,
          bids: allBids.sort((a, b) => 
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          )
        };
      });

      setRequests(processedData);
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError('Failed to load insurance requests');
    } finally {
      setLoading(false);
    }
  }

  if (!user) {
    return (
      <div className="text-center py-8">
        <Shield className="w-12 h-12 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Please Sign In</h3>
        <p className="text-gray-500">You need to be signed in to view your insurance requests.</p>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
          <div className="text-gray-500">Loading requests...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center">
        <AlertCircle className="w-5 h-5 mr-2" />
        {error}
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="text-center py-8">
        <Shield className="w-12 h-12 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Insurance Requests</h3>
        <p className="text-gray-500">There are currently no insurance requests to display.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900">Insurance Requests</h2>
        <span className="text-sm text-gray-500">
          {requests.length} {requests.length === 1 ? 'request' : 'requests'}
        </span>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {requests.map((request) => (
          <div key={request.id} className="bg-white rounded-lg shadow-md p-6">
            {/* Status and Time Section */}
            <div className="mb-4 pb-4 border-b">
              {new Date(request.expires_at) < new Date() ? (
                <div className="text-center">
                  <div className="text-xl font-medium text-red-600">
                    {request.lowest_bid ? (
                      <>
                        Final Price: £{request.lowest_bid.toLocaleString('en-GB', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </>
                    ) : (
                      'Expired'
                    )}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {request.lowest_bid 
                      ? 'Bidding period has ended'
                      : 'No bids were received, resubmit to try again'
                    }
                  </div>
                </div>
              ) : request.lowest_bid !== null ? (
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    £{request.lowest_bid.toLocaleString('en-GB', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    <div>Lowest Bid</div>
                    <div className="text-orange-600 font-medium">{getTimeRemaining(request.expires_at)}</div>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <div className="text-xl font-medium text-gray-400">No Bids Yet</div>
                  <div className="text-sm text-gray-500 mt-1">
                    <div>Waiting for insurers</div>
                    <div className="text-orange-600 font-medium">{getTimeRemaining(request.expires_at)}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Status Badge and Stats */}
            <div className="mb-4">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                request.bid_status === 'awaiting_bids' ? 'bg-blue-100 text-blue-800' :
                request.bid_status === 'bidding_in_progress' ? 'bg-yellow-100 text-yellow-800' :
                request.bid_status === 'bidding_complete' ? 'bg-green-100 text-green-800' :
                'bg-red-100 text-red-800'
              }`}>
                {request.bid_status === 'awaiting_bids' ? 'Awaiting Bids' :
                 request.bid_status === 'bidding_in_progress' ? 'Bidding in Progress' :
                 request.bid_status === 'bidding_complete' ? 'Bidding Complete' :
                 'No Bids'}
              </span>
              <div className="mt-2 flex items-center text-sm text-gray-500">
                <TrendingDown className="w-4 h-4 mr-1" />
                {request.bids?.length || 0} bid{request.bids?.length !== 1 ? 's' : ''} from {request.unique_insurers || 0} insurer{request.unique_insurers !== 1 ? 's' : ''}
                {request.bids?.length > 1 && (() => {
                  const activeBids = request.bids.filter(b => b.status === 'active');
                  const bidsToCompare = new Date(request.expires_at) < new Date() 
                    ? request.bids.filter(b => b.status === 'active' || b.status === 'withdrawn')
                    : activeBids;
                  
                  if (bidsToCompare.length < 2) return null;
                  
                  const highest = Math.max(...bidsToCompare.map(b => b.amount));
                  const lowest = Math.min(...bidsToCompare.map(b => b.amount));
                  const savings = highest - lowest;
                  
                  if (savings <= 0) return null;
                  
                  return (
                    <div className="text-sm text-green-600 font-medium ml-2">
                      £{savings.toLocaleString('en-GB', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })} saved
                    </div>
                  );
                })()}
              </div>
              {request.bids && request.bids.length > 0 && (
                <div className="mt-2">
                  <BidHistoryGraph bids={request.bids} height={100} />
                </div>
              )}
            </div>
              
            {/* Vehicle Details */}
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <h3 className="font-medium text-gray-900">
                  {request.car_make} {request.car_model}
                </h3>
                <button
                  onClick={() => setSelectedRequest(request)}
                  className="text-indigo-600 hover:text-indigo-700 flex items-center"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  View Details
                </button>
              </div>
              <p className="text-sm text-gray-500">
                {request.registration} ({request.car_year})
              </p>
              <div className="flex items-center text-gray-500">
                <Car className="w-4 h-4 mr-2" />
                {request.mileage.toLocaleString()} miles
              </div>
              <div className="flex items-center text-gray-500">
                <Shield className="w-4 h-4 mr-2" />
                {request.no_claims_years} years no claims{request.protect_no_claims && ' (Protected)'} | {request.coverage_type.replace(/-/g, ' ')}
              </div>
              <div className="mt-4 pt-4 border-t text-sm text-gray-500 flex items-center">
                <Shield className="w-4 h-4 mr-2" />
               <strong>{request.name}</strong>
              </div>
            </div>
          </div>
        )).sort((a, b) => {
          // Sort active requests first, then completed, then expired
          const aExpired = new Date(a.expires_at) < new Date();
          const bExpired = new Date(b.expires_at) < new Date();
          
          if (aExpired === bExpired) {
            // If both are expired or both are active, sort by created date
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          }
          
          // Put non-expired requests first
          return aExpired ? 1 : -1;
        })}
      </div>
      
      {/* Request Details Modal */}
      {selectedRequest && (
        <RequestDetailsModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onCancel={handleCancelRequest}
          isLoading={isCancelling}
        />
      )}
    </div>
  );
}